import Highcharts from 'highcharts';
import HighchartsVue from 'highcharts-vue';

// Brand chart theme. Categorical palettes are CVD-validated per mode
// (adjacent-pair ΔE, lightness band, 3:1 contrast on the chart surface).
// Order is the safety mechanism — assign series in this order, never cycled.
const CHART_COLORS = {
  light: ['#5b5bd6', '#12a594', '#d97706', '#d6499a', '#246bfd', '#e8590c', '#7c3aed', '#65a30d'],
  dark: ['#7b7bea', '#109887', '#c67f00', '#d4569c', '#4a83f0', '#d95f2b', '#9a67ec', '#74a12e']
};

const CHART_INK = {
  light: {
    text: '#1a1a2e',
    secondary: '#5a6072',
    muted: '#9aa2b5',
    grid: '#e6e9f2',
    baseline: '#cfd5e4',
    surface: '#ffffff'
  },
  dark: {
    text: '#e4e8f0',
    secondary: '#9ca3af',
    muted: '#6b7280',
    grid: '#374151',
    baseline: '#4b5563',
    surface: '#23272f'
  }
};

function currentMode() {
  const theme = document.documentElement.getAttribute('data-theme');
  if (theme === 'dark') return 'dark';
  if (theme === 'light') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyHighchartsTheme() {
  const mode = currentMode();
  const ink = CHART_INK[mode];
  const fontFamily = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

  Highcharts.setOptions({
    colors: CHART_COLORS[mode],
    chart: {
      backgroundColor: 'transparent',
      style: {
        fontFamily
      }
    },
    title: {
      style: {
        color: ink.text,
        fontSize: '15px',
        fontWeight: '600'
      }
    },
    subtitle: {
      style: {
        color: ink.secondary
      }
    },
    xAxis: {
      lineColor: ink.baseline,
      tickColor: ink.baseline,
      gridLineColor: ink.grid,
      labels: {
        style: {
          color: ink.secondary,
          fontSize: '11px'
        }
      },
      title: {
        style: {
          color: ink.muted
        }
      }
    },
    yAxis: {
      gridLineColor: ink.grid,
      labels: {
        style: {
          color: ink.secondary,
          fontSize: '11px'
        }
      },
      title: {
        style: {
          color: ink.muted
        }
      }
    },
    legend: {
      itemStyle: {
        color: ink.secondary,
        fontWeight: '500',
        fontSize: '12px'
      },
      itemHoverStyle: {
        color: ink.text
      },
      itemHiddenStyle: {
        color: ink.muted
      }
    },
    tooltip: {
      backgroundColor: '#1a1a2e',
      borderWidth: 0,
      borderRadius: 8,
      shadow: false,
      style: {
        color: '#ffffff',
        fontSize: '12px'
      }
    },
    plotOptions: {
      series: {
        borderRadius: 3
      },
      column: {
        borderWidth: 1,
        borderColor: ink.surface
      },
      bar: {
        borderWidth: 1,
        borderColor: ink.surface
      },
      pie: {
        borderWidth: 2,
        borderColor: ink.surface
      },
      line: {
        lineWidth: 2
      },
      spline: {
        lineWidth: 2
      }
    },
    credits: {
      enabled: false
    }
  });
}

export function initHighcharts(app) {
  /**
   * Highcharts custom legend color
   * http://jsfiddle.net/fypopsnc/5/
   */
  (function(H) {
    H.wrap(H.Legend.prototype, 'colorizeItem', function(proceed, item) {
      var color = item.color;
      if (item.options.legendColor) {
        item.color = item.options.legendColor;
      }
      proceed.apply(this, Array.prototype.slice.call(arguments, 1));
      item.color = color;
    });
  }(Highcharts));

  applyHighchartsTheme();

  app.use(HighchartsVue);
}
